import { Section, CodeBlock, InfoBox } from "@/components"
import { Shield, Key, Lock, RefreshCw } from "lucide-react"

export function MongoDBAuthenticationPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Authentication System</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Learn about the complete authentication system with JWT tokens, OAuth, 
          role-based access control, and security best practices.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <AuthFeature
          icon={<Key className="h-6 w-6 text-primary" />}
          title="JWT Access & Refresh Tokens"
          description="Secure token-based authentication with automatic rotation"
        />
        <AuthFeature
          icon={<Shield className="h-6 w-6 text-primary" />}
          title="OAuth 2.0 Integration"
          description="Google OAuth login with automatic user creation"
        />
        <AuthFeature
          icon={<Lock className="h-6 w-6 text-primary" />}
          title="Role-Based Access Control"
          description="Three-tier role system: USER, ADMIN, SUPER_ADMIN"
        />
        <AuthFeature
          icon={<RefreshCw className="h-6 w-6 text-primary" />}
          title="Device Tracking"
          description="Track and manage user sessions across devices"
        />
      </div>

      <Section title="Authentication Flow" description="How the system works">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">1. Registration</h3>
            <CodeBlock
              code={`POST /api/auth/register
{
  "email": "user@example.com",
  "password": "SecureP@ssw0rd",
  "firstName": "John",
  "lastName": "Doe"
}`}
              language="json"
            />
            <p className="text-sm text-muted-foreground">
              Creates a new user account with hashed password (bcrypt, 12 rounds).
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">2. Login</h3>
            <CodeBlock
              code={`POST /api/auth/login
{
  "email": "user@example.com",
  "password": "SecureP@ssw0rd"
}

Response:
{
  "statusCode": 200,
  "message": "Login successful",
  "data": {
    "accessToken": "eyJhbGciOiJIUzI1NiIs...",
    "refreshToken": "eyJhbGciOiJIUzI1NiIs...",
    "user": {
      "id": "507f1f77bcf86cd799439011",
      "email": "user@example.com",
      "firstName": "John",
      "lastName": "Doe",
      "role": "USER"
    }
  }
}`}
              language="json"
            />
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">3. Protected Requests</h3>
            <CodeBlock
              code={`GET /api/user/profile
Authorization: Bearer eyJhbGciOiJIUzI1NiIs...

Response:
{
  "statusCode": 200,
  "data": {
    "id": "507f1f77bcf86cd799439011",
    "email": "user@example.com",
    "firstName": "John",
    "lastName": "Doe",
    "role": "USER"
  }
}`}
              language="json"
            />
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">4. Token Refresh</h3>
            <CodeBlock
              code={`POST /api/auth/refresh
{
  "refreshToken": "eyJhbGciOiJIUzI1NiIs..."
}

Response:
{
  "statusCode": 200,
  "data": {
    "accessToken": "eyJhbGciOiJIUzI1NiIs...",
    "refreshToken": "eyJhbGciOiJIUzI1NiIs..."
  }
}`}
              language="json"
            />
            <p className="text-sm text-muted-foreground">
              When the access token expires (default: 15 minutes), use the refresh token to get a new pair.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Google OAuth" description="Social authentication">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Users can sign in with their Google account. The system automatically creates 
            a user account if it doesn't exist.
          </p>

          <CodeBlock
            code={`// Redirect user to Google login
GET /api/auth/google

// Google redirects back to callback URL
GET /api/auth/google/callback?code=...

Response:
{
  "statusCode": 200,
  "data": {
    "accessToken": "eyJhbGciOiJIUzI1NiIs...",
    "refreshToken": "eyJhbGciOiJIUzI1NiIs...",
    "user": {
      "id": "507f1f77bcf86cd799439011",
      "email": "user@gmail.com",
      "firstName": "John",
      "lastName": "Doe",
      "role": "USER",
      "providers": ["GOOGLE"]
    }
  }
}`}
            language="typescript"
          />

          <InfoBox type="info" title="Multiple Providers">
            Users can link multiple OAuth providers to the same account. The system tracks 
            which providers are connected in the User document.
          </InfoBox>
        </div>
      </Section>

      <Section title="Role-Based Access Control" description="Authorization system">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The template includes a flexible role-based access control system with three predefined roles.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <RoleCard
              role="USER"
              color="bg-blue-500/10 text-blue-500"
              permissions={[
                "Access own profile",
                "Update own information",
                "Use all public endpoints",
              ]}
            />
            <RoleCard
              role="ADMIN"
              color="bg-purple-500/10 text-purple-500"
              permissions={[
                "All USER permissions",
                "Manage other users",
                "Access admin endpoints",
              ]}
            />
            <RoleCard
              role="SUPER_ADMIN"
              color="bg-red-500/10 text-red-500"
              permissions={[
                "All ADMIN permissions",
                "Manage admins",
                "System configuration",
              ]}
            />
          </div>

          <div className="space-y-4 mt-8">
            <h3 className="font-semibold text-lg">Using Roles in Your Code</h3>
            <CodeBlock
              code={`import { Roles } from '@/shared/decorators/roles.decorator';
import { Role } from '@/shared/enums/role.enum';

@Controller('admin')
export class AdminController {
  // Only ADMIN and SUPER_ADMIN can access
  @Roles(Role.ADMIN, Role.SUPER_ADMIN)
  @Get('users')
  getUsers() {
    return this.adminService.getUsers();
  }

  // Only SUPER_ADMIN can access
  @Roles(Role.SUPER_ADMIN)
  @Delete('users/:id')
  deleteUser(@Param('id') id: string) {
    return this.adminService.deleteUser(id);
  }
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Security Features" description="Built-in protections">
        <div className="grid md:grid-cols-2 gap-6">
          <SecurityFeature
            title="Password Hashing"
            description="All passwords are hashed with bcrypt (12 rounds) before storage. Never stored in plain text."
          />
          <SecurityFeature
            title="Token Rotation"
            description="Refresh tokens are rotated on each use, preventing token reuse attacks."
          />
          <SecurityFeature
            title="Device Tracking"
            description="Track user sessions by device for security monitoring and management."
          />
          <SecurityFeature
            title="CORS Protection"
            description="Configured CORS to only allow requests from trusted origins."
          />
          <SecurityFeature
            title="Rate Limiting"
            description="Prevent brute force attacks with configurable rate limiting."
          />
          <SecurityFeature
            title="Input Validation"
            description="All inputs are validated with class-validator before processing."
          />
        </div>
      </Section>

      <Section title="Custom Decorators" description="Simplify authentication checks">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">@Public()</h3>
            <p className="text-sm text-muted-foreground">
              Mark an endpoint as public (no authentication required).
            </p>
            <CodeBlock
              code={`import { Public } from '@/shared/decorators/public.decorator';

@Public()
@Get('health')
getHealth() {
  return { status: 'ok' };
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">@CurrentUser()</h3>
            <p className="text-sm text-muted-foreground">
              Access the authenticated user in your route handlers.
            </p>
            <CodeBlock
              code={`import { CurrentUser } from '@/auth/decorators/current-user.decorator';

@Get('profile')
getProfile(@CurrentUser() user: User) {
  return user;
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">@Roles()</h3>
            <p className="text-sm text-muted-foreground">
              Restrict access to specific roles.
            </p>
            <CodeBlock
              code={`import { Roles } from '@/shared/decorators/roles.decorator';
import { Role } from '@/shared/enums/role.enum';

@Roles(Role.ADMIN)
@Get('admin/dashboard')
getAdminDashboard() {
  return this.adminService.getDashboard();
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>
    </div>
  )
}

function AuthFeature({ 
  icon, 
  title, 
  description 
}: { 
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-3">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  )
}

function RoleCard({ 
  role, 
  color, 
  permissions 
}: { 
  role: string
  color: string
  permissions: string[]
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${color}`}>
        {role}
      </div>
      <ul className="space-y-2">
        {permissions.map((permission, index) => (
          <li key={index} className="flex gap-2 text-sm text-muted-foreground">
            <span className="text-primary">•</span>
            <span>{permission}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SecurityFeature({ 
  title, 
  description 
}: { 
  title: string
  description: string
}) {
  return (
    <div className="bg-muted/30 rounded-lg p-6 space-y-2">
      <h3 className="font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  )
}

